"use client";

import { useCallback, useEffect, useState } from "react";
import { HiMagnifyingGlass, HiMiniChevronDown } from "react-icons/hi2";
import GroupOrCategoryPopUp from "../popup/groupOrCategoryPopUp";
import StatusPopUp from "../popup/statusPopUp";
import categoryAndGroupAPI from "../../services/categoryAndGroup/categoryAndGroupAPI";
import { useTranslations } from "next-intl";

type LibraryHeaderFiltersProps = {
    title: string;
    availableSubjects?: string[];
    availableGroupTopics?: string[];
    availableGameTypeKeys?: string[];
    showCategoryGroupButtons?: boolean;
    onFilterChange?: (filters: FilterState) => void;
};

export type FilterState = {
    search: string;
    subject: string;
    groupTopic: string;
    gameType: string;
    questionMode: string;
    playMode: string;
};

type SelectFilterProps = {
    value: string;
    onChange: (value: string) => void;
    options: Array<{ value: string; label: string }>;
    className?: string;
};

export const ALL_SUBJECT_FILTER = "__all_subject__";
export const ALL_GROUP_FILTER = "__all_group__";
export const ALL_GAME_TYPE_FILTER = "__all_game_type__";
export const ALL_QUESTION_MODE_FILTER = "__all_question_mode__";
export const QUESTION_MODE_SERIES_FILTER = "series_question";
export const QUESTION_MODE_SINGLE_FILTER = "single_question";

const USER_STORAGE_CHANGED_EVENT = "user-storage-changed";

const GAME_TYPE_OPTIONS_ORDER = [
    "find_the_match",
    "game_show_quiz",
    "flip_cards",
    "air_plan/flying_fruits",
    "flappy_bird",
    "whack_a_mole",
    "anagram",
    "situation",
];

const GAME_TYPE_OPTIONS_SHOW = [
    "Find the Match",
    "Game Show Quiz",
    "Flip Cards",
    "Flying Fruits",
    "Flappy Chicken",
    "Whack a Mole",
    "Anagram",
    "Situation",
];


function getTeacherUuidFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";

        const parsed = JSON.parse(raw) as { id?: string };
        if (!parsed || typeof parsed !== "object") return "";

        return typeof parsed.id === "string" ? parsed.id.trim() : "";
    } catch {
        return "";
    }
}

function getUserRoleFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";

        const parsed = JSON.parse(raw) as { role?: string };
        if (!parsed || typeof parsed !== "object") return "";

        return typeof parsed.role === "string" ? parsed.role.trim() : "";
    } catch {
        return "";
    }
}

function getRowsFromApiResponse(response: unknown) {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray((response as { data?: unknown[] })?.data)) {
        return (response as { data: unknown[] }).data;
    }

    return [];
}

function SelectFilter({ value, onChange, options, className = "" }: SelectFilterProps) {
    return (
        <div className={`relative min-w-0 w-full ${className}`}>
            <select
                value={value}
                onChange={(event) => onChange(event.target.value)}
                className="h-10 w-full appearance-none rounded-xl border border-neutral-400 bg-white px-4 pr-9 text-sm text-neutral-800 outline-none transition focus:border-gray-500 cursor-pointer"
            >
                {options.map((option) => {
                    return (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    );
                })}
            </select>
            <HiMiniChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-600" />
        </div>
    );
}

export default function LibraryHeaderFilters({
    title,
    availableSubjects = [],
    availableGroupTopics = [],
    availableGameTypeKeys = [],
    showCategoryGroupButtons = false,
    onFilterChange,
}: LibraryHeaderFiltersProps) {
    const tMain = useTranslations("main");
    const subjectPlaceholderLabel = tMain("subject");
    const groupPlaceholderLabel = tMain("topic");
    const gameTypePlaceholderLabel = tMain("gameType");
    const questionModePlaceholderLabel = tMain("questionType");
    const singleQuestionModeLabel = tMain("single");
    const sequentialQuestionModeLabel = tMain("sequential");

    const questionModeOptions = [
        { value: ALL_QUESTION_MODE_FILTER, label: questionModePlaceholderLabel },
        { value: QUESTION_MODE_SINGLE_FILTER, label: singleQuestionModeLabel },
        { value: QUESTION_MODE_SERIES_FILTER, label: sequentialQuestionModeLabel },
    ];

    const [activePopUp, setActivePopUp] = useState<"category" | "group" | null>(null);
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);
    const [filters, setFilters] = useState<FilterState>({
        search: "",
        subject: ALL_SUBJECT_FILTER,
        groupTopic: ALL_GROUP_FILTER,
        gameType: ALL_GAME_TYPE_FILTER,
        questionMode: ALL_QUESTION_MODE_FILTER,
        playMode: "ทั้งหมด",
    });
    const [subjectOptions, setSubjectOptions] = useState<Array<{ value: string; label: string }>>([
        { value: ALL_SUBJECT_FILTER, label: subjectPlaceholderLabel },
    ]);
    const [groupOptions, setGroupOptions] = useState<Array<{ value: string; label: string }>>([
        { value: ALL_GROUP_FILTER, label: groupPlaceholderLabel },
    ]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => {
        onFilterChange?.(filters);
    }, [filters, onFilterChange]);

    const loadCategoryAndGroupOptions = useCallback(async () => {
        const teacherUuid = getTeacherUuidFromLocalStorage();
        if (!teacherUuid) {
            setSubjectOptions([{ value: ALL_SUBJECT_FILTER, label: subjectPlaceholderLabel }]);
            setGroupOptions([{ value: ALL_GROUP_FILTER, label: groupPlaceholderLabel }]);
            return;
        }

        // const [categoriesResponse, groupsResponse] = await Promise.all([
        //     mainAPI.getCategoriesByTeacherUuid(teacherUuid),
        //     mainAPI.getGroupsByTeacherUuid(teacherUuid),
        // ]);

        const [categoriesResponse, groupsResponse] = await Promise.all([
            categoryAndGroupAPI.getCategoriesByTeacherUuid(teacherUuid),
            categoryAndGroupAPI.getGroupsByTeacherUuid(teacherUuid),
        ]);

        const categoryRows = getRowsFromApiResponse(categoriesResponse);
        const groupRows = getRowsFromApiResponse(groupsResponse);

        const nextSubjectOptions = categoryRows
            .map((item) => String((item as { name?: unknown })?.name ?? "").trim())
            .filter(Boolean);

        const nextGroupOptions = groupRows
            .map((item) => String((item as { name?: unknown })?.name ?? "").trim())
            .filter(Boolean);

        setSubjectOptions([
            { value: ALL_SUBJECT_FILTER, label: subjectPlaceholderLabel },
            ...Array.from(new Set(nextSubjectOptions)).map((name) => ({ value: name, label: name })),
        ]);
        setGroupOptions([
            { value: ALL_GROUP_FILTER, label: groupPlaceholderLabel },
            ...Array.from(new Set(nextGroupOptions)).map((name) => ({ value: name, label: name })),
        ]);
    }, [groupPlaceholderLabel, subjectPlaceholderLabel]);

    useEffect(() => {
        setSubjectOptions((prev) => {
            if (!prev.length) return [{ value: ALL_SUBJECT_FILTER, label: subjectPlaceholderLabel }];
            return prev.map((option, index) =>
                index === 0 ? { ...option, label: subjectPlaceholderLabel } : option,
            );
        });

        setGroupOptions((prev) => {
            if (!prev.length) return [{ value: ALL_GROUP_FILTER, label: groupPlaceholderLabel }];
            return prev.map((option, index) =>
                index === 0 ? { ...option, label: groupPlaceholderLabel } : option,
            );
        });
    }, [groupPlaceholderLabel, subjectPlaceholderLabel]);

    useEffect(() => {
        const hasSelectedSubject = subjectOptions.some((option) => option.value === filters.subject);
        if (filters.subject !== ALL_SUBJECT_FILTER && !hasSelectedSubject) {
            setFilters((prev) => ({ ...prev, subject: ALL_SUBJECT_FILTER }));
        }
    }, [filters.subject, subjectOptions]);

    useEffect(() => {
        const hasSelectedGroup = groupOptions.some((option) => option.value === filters.groupTopic);
        if (filters.groupTopic !== ALL_GROUP_FILTER && !hasSelectedGroup) {
            setFilters((prev) => ({ ...prev, groupTopic: ALL_GROUP_FILTER }));
        }
    }, [filters.groupTopic, groupOptions]);

    useEffect(() => {
        void loadCategoryAndGroupOptions();
    }, [loadCategoryAndGroupOptions]);

    useEffect(() => {
        const onUserStorageChanged = () => {
            setIsSuperAdmin(getUserRoleFromLocalStorage() === "super_admin");
            void loadCategoryAndGroupOptions();
        };

        setIsSuperAdmin(getUserRoleFromLocalStorage() === "super_admin");
        window.addEventListener("storage", onUserStorageChanged);
        window.addEventListener(USER_STORAGE_CHANGED_EVENT, onUserStorageChanged);

        return () => {
            window.removeEventListener("storage", onUserStorageChanged);
            window.removeEventListener(USER_STORAGE_CHANGED_EVENT, onUserStorageChanged);
        };
    }, [loadCategoryAndGroupOptions]);

    const gameTypeOptions: Array<{ value: string; label: string }> = [
        { value: ALL_GAME_TYPE_FILTER, label: gameTypePlaceholderLabel },
        ...GAME_TYPE_OPTIONS_ORDER
            .map((key, idx) => ({ value: key, label: GAME_TYPE_OPTIONS_SHOW[idx] }))
            .filter((opt) => availableGameTypeKeys.includes(opt.value)),
    ];

    useEffect(() => {
        const isValid = gameTypeOptions.some((opt) =>
            typeof opt === "string" ? opt === filters.gameType : opt.value === filters.gameType,
        );

        if (filters.gameType !== ALL_GAME_TYPE_FILTER && !isValid) {
            setFilters((prev) => ({ ...prev, gameType: ALL_GAME_TYPE_FILTER }));
        }
    }, [filters.gameType, gameTypeOptions]);

    useEffect(() => {
        const isValid = questionModeOptions.some((option) => option.value === filters.questionMode);
        if (!isValid) {
            setFilters((prev) => ({ ...prev, questionMode: ALL_QUESTION_MODE_FILTER }));
        }
    }, [filters.questionMode, questionModeOptions]);

    const closePopUp = () => {
        setActivePopUp(null);
    };

    const handleChangedCategoryOrGroup = async () => {
        void loadCategoryAndGroupOptions();
    };

    const popUpTitle =
        activePopUp === "category"
            ? tMain("addSubject")
            : tMain("addTopic");

    return (
        <>
            <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
                <h1 className="text-2xl font-semibold text-[#4E4E4E] sm:text-3xl lg:text-4xl">{title}</h1>

                {showCategoryGroupButtons ? (
                    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:gap-4">
                        <button
                            type="button"
                            onClick={() => setActivePopUp("category")}
                            className="h-10 w-full cursor-pointer rounded-xl bg-[#FDEF5C] text-sm font-semibold text-[#2B2B2B] hover:bg-[#fce94f] sm:h-12 sm:w-[130px] sm:text-base"
                        >
                            + {tMain("subject")}
                        </button>

                        <button
                            type="button"
                            onClick={() => setActivePopUp("group")}
                            className="h-10 w-full cursor-pointer rounded-xl bg-[#FDEF5C] text-sm font-semibold text-[#2B2B2B] hover:bg-[#fce94f] sm:h-12 sm:w-[130px] sm:text-base"
                        >
                            + {tMain("topic")}
                        </button>
                    </div>
                ) : null}
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-end gap-2 sm:mt-4 sm:gap-4">
                <button
                    type="button"
                    onClick={() => setFilters({
                        search: "",
                        subject: ALL_SUBJECT_FILTER,
                        groupTopic: ALL_GROUP_FILTER,
                        gameType: ALL_GAME_TYPE_FILTER,
                        questionMode: ALL_QUESTION_MODE_FILTER,
                        playMode: "ทั้งหมด",
                    })}
                    className="h-10 w-full cursor-pointer rounded-xl bg-[#0092d7] text-sm font-semibold text-[#FFFFFF] hover:bg-[#0080c1] sm:h-12 sm:w-[130px] sm:text-base"
                >
                    {tMain("resetFilters")}
                </button>
            </div>

            <div className="mt-4 grid gap-2 sm:gap-3 md:grid-cols-2 xl:grid-cols-[420px_repeat(4,minmax(90px,1fr))]">
                <div className="flex h-10 w-full items-center justify-between rounded-xl border border-neutral-400 bg-white px-4 text-sm text-neutral-800 focus-within:border-gray-500 cursor-pointer">
                    <input
                        type="text"
                        value={filters.search}
                        onChange={(event) =>
                            setFilters((prev) => ({ ...prev, search: event.target.value }))
                        }
                        placeholder={tMain("search")}
                        className="w-full bg-transparent outline-none"
                    />
                    <HiMagnifyingGlass className="text-lg text-neutral-600" />
                </div>

                <SelectFilter
                    value={filters.subject}
                    onChange={(value) => setFilters((prev) => ({ ...prev, subject: value }))}
                    options={subjectOptions}
                />

                <SelectFilter
                    value={filters.groupTopic}
                    onChange={(value) =>
                        setFilters((prev) => ({ ...prev, groupTopic: value }))
                    }
                    options={groupOptions}
                />

                <SelectFilter
                    value={filters.gameType}
                    onChange={(value) => setFilters((prev) => ({ ...prev, gameType: value }))}
                    options={gameTypeOptions}
                />

                <SelectFilter
                    value={filters.questionMode}
                    onChange={(value) =>
                        setFilters((prev) => ({ ...prev, questionMode: value }))
                    }
                    options={questionModeOptions}
                />

            </div>

            {activePopUp ? (
                <GroupOrCategoryPopUp
                    title={popUpTitle}
                    onClose={closePopUp}
                    onConfirm={handleChangedCategoryOrGroup}
                    type={activePopUp}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={() => setStatusPopUp(null)}
                />
            ) : null}
        </>
    );
}